import { useEffect, useState } from 'react';
import { Button, Modal, Popover, Steps } from 'antd';
import type { StepsProps } from 'antd';

import { Category } from './Category';
import { Question } from './Question';
import { Goals } from '../Goals';
import {
  DATA_CAREER_ADVANCEMENTS,
  DATA_CAREER_EDUCATION_OBJECTIVES,
  DATA_HEALT_GOALS,
  DATA_HEALT_WELLNESS,
  DATA_FINANCE_GOALS,
  DATA_FINANCE_INVESTMENTS,
  DATA_PERSONAL_RELATIONSHIPS,
  DATA_PERSONAL_EVENT,
  DATA_PERSONAL_DEVELOPMENT,
  DATA_PERSONAL_HABITS,
  DATA_LEISURE_HOBBIES,
  DATA_LEISURE_EXPERIENCE,
  DATA_COMMUNITY_GOALS,
  DATA_COMMUNITY_ISSUES,
  DATA_LIFE_MILESTONES,
  DATA_LIFE_ISSUES,
  CATEGORIES,
} from './data';
import { ModalContent } from '../ModalContent';

import './Questions.css';

type StepsTypes = {
  title: string,
  description: string,
};

const DEFAULT_FORM_STATE_QUESTIONS: Array<Array<string[]>> = [
  [
    // Career
    [DATA_CAREER_ADVANCEMENTS.title],
    [DATA_CAREER_EDUCATION_OBJECTIVES.title],
  ],
  [
    // Health
    [DATA_HEALT_GOALS.title],
    [DATA_HEALT_WELLNESS.title],
  ],
  [
    // Financial stability
    [DATA_FINANCE_GOALS.title],
    [DATA_FINANCE_INVESTMENTS.title],
  ],
  [
    // Personal relationships
    [DATA_PERSONAL_RELATIONSHIPS.title],
    [DATA_PERSONAL_EVENT.title],
  ],
  [
    // Personal development
    [DATA_PERSONAL_DEVELOPMENT.title],
    [DATA_PERSONAL_HABITS.title],
  ],
  [
    // Leisure
    [DATA_LEISURE_HOBBIES.title],
    [DATA_LEISURE_EXPERIENCE.title],
  ],
  [
    // Community
    [DATA_COMMUNITY_GOALS.title],
    [DATA_COMMUNITY_ISSUES.title],
  ],
  [
    // Life
    [DATA_LIFE_MILESTONES.title],
    [DATA_LIFE_ISSUES.title],
  ],
];

const DEFAULT_FORM_STATE: Array<Array<string[]>> = [
  [
    // Career
    [],
    [],
  ],
  [
    // Health
    [],
    [],
  ],
  [
    // Financial stability
    [],
    [],
  ],
  [
    // Personal relationships
    [],
    [],
  ],
  [
    // Personal development
    [],
    [],
  ],
  [
    // Leisure
    [],
    [],
  ],
  [
    // Community
    [],
    [],
  ],
  [
    // Life
    [],
    [],
  ],
];

export type UpdateTypes = {
  selectedAnswers: string[],
  categoryIndex: number,
  questionIndex: number,
};

export const Questions = () => {
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [goals, setGoals] = useState(null);
  const [error, setError] = useState('');

  const buttonElement = document.getElementById('button');
  const footerElement = document.getElementById('footer');

  const checkScrollPosition = () => {
    const questionsElement = document.getElementById('questions');

    if (!questionsElement) return;
    const bounding = questionsElement?.getBoundingClientRect();
    // Consider the offset if any fixed/sticky elements cover the element
    const offsetTop = bounding?.top - 500;
    const offsetBottom = bounding?.bottom - 500;

    if (offsetTop >= 0 || offsetBottom < 0) {
      // If the scroll is more than 100px from the top of the page, add the class
      buttonElement?.classList.add('fixed');
    } else {
      // If it's less than 100px from the top, remove the class
      buttonElement?.classList.remove('fixed');
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', checkScrollPosition);

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const scrollToElement = (elementId: string) => {
    // Ensure the element exists
    var element = document.getElementById(elementId);
    if (!element) return;
  
    // Calculate the position again in case content has changed
    var bounding = element.getBoundingClientRect();
  
    // Consider the offset if any fixed/sticky elements cover the element
    var offset = bounding.top + window.pageYOffset - /* fixedElementHeight */ 0;
  
    // Scroll to the target element
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  }

  const goToNextQuestion = async () => {
    // if we are on the first question move to the second question
    if (currentQuestionIndex === 0) {
      await setCurrentQuestionIndex(1);
    }

    // Else move to the new category
    if (formState[currentCategoryIndex][0].length > 0 &&
      formState[currentCategoryIndex][1].length > 0) {
        setCurrentCategoryIndex(currentCategoryIndex + 1);
        setCurrentQuestionIndex(0);
    }

    await scrollToElement('questions');
  };

  const disableButton = formState[currentCategoryIndex] &&
    formState[currentCategoryIndex][currentQuestionIndex] &&
    formState[currentCategoryIndex][currentQuestionIndex].length === 0;

  const renderButton = () => {
    // return <Button
    //     size="middle"
    //     type="primary"
    //     onClick={showModal}
    //     className="questions__cta"
    //   >Get My Goals</Button>;

    if (currentCategoryIndex === formState.length - 1 &&
      formState[formState.length - 1][0].length > 0 &&
      formState[formState.length - 1][1].length > 0
    ) {
      return <Button
        id="button"
        size="middle"
        type="primary"
        onClick={showModal}
        className="questions__cta"
      >Get My Goals</Button>;
    }

    return <Button
      id="button"
      size="middle"
      type="primary"
      onClick={goToNextQuestion}
      className="questions__cta"
      disabled={disableButton}
    >Next Question</Button>
  };

  const updateFormState = async ({
    selectedAnswers,
    categoryIndex,
    questionIndex,
  }: UpdateTypes) => {
    const tempFormState = [...formState];

    if (tempFormState[categoryIndex] &&
      tempFormState[categoryIndex][questionIndex]
    ) {
      tempFormState[categoryIndex][questionIndex] = selectedAnswers;
    }
    await setFormState([
      ...formState,

    ])
  };

  const generateBodyContent = () => {
    let prompt = '';

    formState.forEach((category, index) => {
      prompt += `${CATEGORIES[index]}: \r\n`;

      category.forEach((answer, answerIndex) => {
        prompt += `${DEFAULT_FORM_STATE_QUESTIONS[index][answerIndex]}: ${answer}\r\n`;
      });
    });

    return {
      goals: prompt,
    };

    // return {
    //   goals: `Career & Education:
    //     Are there any career advancements (e.g., promotion, new skills) you aim to achieve this year?: Starting a side business or consulting practice to explore entrepreneurial interests
    //     Do you have any educational objectives, such as enrolling in a course or obtaining a certification?: Taking a creative writing class to improve storytelling abilities for content creation.
    //     Health & Wellness: 
    //     What are your health goals (e.g., lose weight, run a marathon)?:Eat a balanced diet with an emphasis on whole foods and proper nutrition.,Drink more water daily and stay well-hydrated.,Cut down on sugar intake and reduce processed foods.
    //     Are there wellness practices (e.g., meditation, improved sleep) you wish to incorporate into your routine?: Improved Sleep Hygiene: Establish a regular sleep schedule and create a bedtime ritual to enhance sleep quality.,Healthy Eating Habits: Focus on a balanced diet with nutrient-dense foods and appropriate portion sizes.,Journaling: Keep a daily journal to reflect on thoughts, feelings, and personal growth.
    //     Financial Stability: 
    //     Do you have specific financial goals (e.g., saving a certain amount, reducing debt)?: Budgeting: Implement a strict monthly budget to better control and track spending.
    //     Are there investments or financial planning activities you want to undertake?: Debt Management Plan: Develop a plan to prioritize and accelerate the repayment of high-interest debt.
    //     Personal Relationships: 
    //     Are you looking to improve or change any relationships (e.g., family bonds, making new friends)?: Engage in active listening and show more empathy in daily interactions with others.,Strengthen communication with a partner or spouse through regular check-ins and date nights.
    //     Is there a significant relationship event you are working towards (e.g., marriage, reconciliation)?: Arranging a family reunion to reconnect with extended relatives.
    //     Personal Development: 
    //     What are your self-improvement priorities (e.g., learning a language, reading more)?: Reading a set number of books each month for personal development and knowledge growth.,Engaging in daily meditation or mindfulness practices to reduce stress.
    //     Are there any personal habits you wish to develop or break?: Develop: Establish a morning routine that includes exercise and a healthy breakfast.,Break: Overcome procrastination by implementing productivity techniques.,Develop: Practice regular journaling or reflection for mental clarity and goal tracking.,Break: Cut down on social media usage to reduce screen time and increase presence.
    //     Leisure & Recreation: 
    //     Are there any hobbies or recreational activities (e.g., travel, arts) you plan to start or continue?: Writing: Start a blog, write short stories, or work on a novel.
    //     Is there an experience or challenge you're aiming for (e.g., a trip abroad, a creative project)?: Entrepreneurship: Start a new business or launch an innovative product or service in the market.
    //     Community & Contribution: 
    //     Do you have any goals related to giving back to the community or volunteering?: Coach a youth sports team or lead an after-school program.
    //     Are there social issues or causes you would like to be more involved with?: Homelessness and Housing Insecurity
    //     Life Milestones: 
    //     Are there any key life milestones (e.g., buying a house, starting a family) you aim to achieve?: Paying off a significant debt, like a student loan or mortgage.
    //     Do you wish to make any major lifestyle changes this year?: Working towards financial independence to reduce reliance on a traditional 9-to-5 job.,Pursuing a digital nomad lifestyle to travel while working remotely.`
    // };
  };

  const handleOk = async () => {
    setIsLoading(true);
    const bodyContent = await generateBodyContent();

    // const mockData = {
    //   prompt: "Can you generate my SMART goals for 2024 based on my answers to this questionnaire? return data as JSON provided in the function_call, each goal as separate array field. You should generate only as many goals as many answers you get for each question/category: Career & Education:\n        Are there any career advancements (e.g., promotion, new skills) you aim to achieve this year?: Starting a side business or consulting practice to explore entrepreneurial interests\n        Do you have any educational objectives, such as enrolling in a course or obtaining a certification?: Taking a creative writing class to improve storytelling abilities for content creation.\n        Health & Wellness: \n        What are your health goals (e.g., lose weight, run a marathon)?: Eat a balanced diet with an emphasis on whole foods and proper nutrition.,Drink more water daily and stay well-hydrated.,Cut down on sugar intake and reduce processed foods.\n        Are there wellness practices (e.g., meditation, improved sleep) you wish to incorporate into your routine?: Improved Sleep Hygiene: Establish a regular sleep schedule and create a bedtime ritual to enhance sleep quality.,Healthy Eating Habits: Focus on a balanced diet with nutrient-dense foods and appropriate portion sizes.,Journaling: Keep a daily journal to reflect on thoughts, feelings, and personal growth.\n        Financial Stability: \n        Do you have specific financial goals (e.g., saving a certain amount, reducing debt)?: Budgeting: Implement a strict monthly budget to better control and track spending.\n        Are there investments or financial planning activities you want to undertake?: Debt Management Plan: Develop a plan to prioritize and accelerate the repayment of high-interest debt.\n        Personal Relationships: \n        Are you looking to improve or change any relationships (e.g., family bonds, making new friends)?: Engage in active listening and show more empathy in daily interactions with others.,Strengthen communication with a partner or spouse through regular check-ins and date nights.\n        Is there a significant relationship event you are working towards (e.g., marriage, reconciliation)?: Arranging a family reunion to reconnect with extended relatives.\n        Personal Development: \n        What are your self-improvement priorities (e.g., learning a language, reading more)?: Reading a set number of books each month for personal development and knowledge growth.,Engaging in daily meditation or mindfulness practices to reduce stress.\n        Are there any personal habits you wish to develop or break?: Develop: Establish a morning routine that includes exercise and a healthy breakfast.,Break: Overcome procrastination by implementing productivity techniques.,Develop: Practice regular journaling or reflection for mental clarity and goal tracking.,Break: Cut down on social media usage to reduce screen time and increase presence.\n        Leisure & Recreation: \n        Are there any hobbies or recreational activities (e.g., travel, arts) you plan to start or continue?: Writing: Start a blog, write short stories, or work on a novel.\n        Is there an experience or challenge you're aiming for (e.g., a trip abroad, a creative project)?: Entrepreneurship: Start a new business or launch an innovative product or service in the market.\n        Community & Contribution: \n        Do you have any goals related to giving back to the community or volunteering?: Coach a youth sports team or lead an after-school program.\n        Are there social issues or causes you would like to be more involved with?: Homelessness and Housing Insecurity\n        Life Milestones: \n        Are there any key life milestones (e.g., buying a house, starting a family) you aim to achieve?: Paying off a significant debt, like a student loan or mortgage.\n        Do you wish to make any major lifestyle changes this year?: Working towards financial independence to reduce reliance on a traditional 9-to-5 job.,Pursuing a digital nomad lifestyle to travel while working remotely.",
    //   promptResponse: "{\n  \"goals\": [\n    {\n      \"category\": \"Career & Education\",\n      \"description\": \"Starting a side business or consulting practice to explore entrepreneurial interests\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Career & Education\",\n      \"description\": \"Taking a creative writing class to improve storytelling abilities for content creation\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Eat a balanced diet with an emphasis on whole foods and proper nutrition\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Drink more water daily and stay well-hydrated\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Cut down on sugar intake and reduce processed foods\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Improved Sleep Hygiene: Establish a regular sleep schedule and create a bedtime ritual to enhance sleep quality\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Healthy Eating Habits: Focus on a balanced diet with nutrient-dense foods and appropriate portion sizes\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Health & Wellness\",\n      \"description\": \"Journaling: Keep a daily journal to reflect on thoughts, feelings, and personal growth\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Financial Stability\",\n      \"description\": \"Budgeting: Implement a strict monthly budget to better control and track spending\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Financial Stability\",\n      \"description\": \"Debt Management Plan: Develop a plan to prioritize and accelerate the repayment of high-interest debt\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Relationships\",\n      \"description\": \"Engage in active listening and show more empathy in daily interactions with others\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Relationships\",\n      \"description\": \"Strengthen communication with a partner or spouse through regular check-ins and date nights\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Relationships\",\n      \"description\": \"Arranging a family reunion to reconnect with extended relatives\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Reading a set number of books each month for personal development and knowledge growth\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Engaging in daily meditation or mindfulness practices to reduce stress\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Develop: Establish a morning routine that includes exercise and a healthy breakfast\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Break: Overcome procrastination by implementing productivity techniques\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Develop: Practice regular journaling or reflection for mental clarity and goal tracking\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Personal Development\",\n      \"description\": \"Break: Cut down on social media usage to reduce screen time and increase presence\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Leisure & Recreation\",\n      \"description\": \"Writing: Start a blog, write short stories, or work on a novel\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Leisure & Recreation\",\n      \"description\": \"Entrepreneurship: Start a new business or launch an innovative product or service in the market\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Community & Contribution\",\n      \"description\": \"Coach a youth sports team or lead an after-school program\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Community & Contribution\",\n      \"description\": \"Homelessness and Housing Insecurity\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Life Milestones\",\n      \"description\": \"Paying off a significant debt, like a student loan or mortgage\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Life Milestones\",\n      \"description\": \"Working towards financial independence to reduce reliance on a traditional 9-to-5 job\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    },\n    {\n      \"category\": \"Life Milestones\",\n      \"description\": \"Pursuing a digital nomad lifestyle to travel while working remotely\",\n      \"specific\": \"\",\n      \"measurable\": \"\",\n      \"achievable\": \"\",\n      \"relevant\": \"\",\n      \"timeBound\": \"\"\n    }\n  ]\n}",
    // };

    // const parsedData = JSON.parse(mockData?.promptResponse);

    // if (parsedData?.goals) {
    //   setGoals(parsedData?.goals);
    // }

    // setIsLoading(false);

    await fetch('https://us-central1-planmylife-1c7fd.cloudfunctions.net/goalsapi/generate-goals', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyContent),
    })
    .then(response => response.json())
    .then(data => {
      const parsedData = JSON.parse(data?.promptResponse);

      if (parsedData?.goals) {
        setGoals(parsedData?.goals);
      }

      setIsLoading(false);
    })
    .catch((error) => {
      console.error('Error:', error);
      setError("Oops! Our AI is having a moment and couldn't load your goals. Maybe it's daydreaming about becoming human.");
      setIsLoading(false);
    });
  };

  if (goals) {
    return <Goals data={goals} />
  }

  // const steps:StepsTypes[] = [];

  // CATEGORIES.forEach((item, index) => {
  //   steps.push({
  //     title: item,
  //     description: currentCategoryIndex > index ? 'Completed' : 'In progress',
  //   });
  // });

  return (
    <div id="questions" className="questions">
      <div className="questions__header">
        <h2>Personal Goals Questionnaire</h2>
        <p className="questions__subheading">Please take a moment to reflect on what you would like to achieve in the upcoming year. This short questionnaire will help you articulate your high-level personal goals.</p>

        {renderButton()}

        <div className="questions__stepper">
          <p className="questions__step">{`${CATEGORIES.length - (currentCategoryIndex)} left out of ${CATEGORIES.length} questions`}</p>
        </div>

        {currentCategoryIndex >= 7 &&
          <div id="questions__container" className="questions__container">
            <Category title="Life Milestones" />
            { ((formState[7][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 7) &&
              <Question
                data={DATA_LIFE_ISSUES}
                selectedAnswers={formState[7][1]}
                categoryIndex={7}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_LIFE_MILESTONES}
              selectedAnswers={formState[7][0]}
              categoryIndex={7}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 6 &&
          <div className="questions__container">
            <Category title="Community & Contribution" />
            { ((formState[6][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 6) &&
              <Question
                data={DATA_COMMUNITY_ISSUES}
                selectedAnswers={formState[6][1]}
                categoryIndex={6}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_COMMUNITY_GOALS}
              selectedAnswers={formState[6][0]}
              categoryIndex={6}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 5 &&
          <div className="questions__container">
            <Category title="Leisure & Recreation" />
            { ((formState[5][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 5) &&
              <Question
                data={DATA_LEISURE_EXPERIENCE}
                selectedAnswers={formState[5][1]}
                categoryIndex={5}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_LEISURE_HOBBIES}
              selectedAnswers={formState[5][0]}
              categoryIndex={5}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 4 &&
          <div className="questions__container">
            <Category title="Personal Development" />
            { ((formState[4][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 4) &&
              <Question
                data={DATA_PERSONAL_HABITS}
                selectedAnswers={formState[4][1]}
                categoryIndex={4}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_PERSONAL_DEVELOPMENT}
              selectedAnswers={formState[4][0]}
              categoryIndex={4}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 3 &&
          <div className="questions__container">
            <Category title="Personal Relationships" />
            { ((formState[3][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 3) &&
              <Question
                data={DATA_PERSONAL_EVENT}
                selectedAnswers={formState[3][1]}
                categoryIndex={3}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_PERSONAL_RELATIONSHIPS}
              selectedAnswers={formState[3][0]}
              categoryIndex={3}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 2 &&
          <div className="questions__container">
            <Category title="Financial Stability" />
            { ((formState[2][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 2) &&
              <Question
                data={DATA_FINANCE_INVESTMENTS}
                selectedAnswers={formState[2][1]}
                categoryIndex={2}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_FINANCE_GOALS}
              selectedAnswers={formState[2][0]}
              categoryIndex={2}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 1 &&
          <div className="questions__container">
            <Category title="Health & Wellness" />
            { ((formState[1][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 1) &&
              <Question
                data={DATA_HEALT_WELLNESS}
                selectedAnswers={formState[1][1]}
                categoryIndex={1}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_HEALT_GOALS}
              selectedAnswers={formState[1][0]}
              categoryIndex={1}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }

        {currentCategoryIndex >= 0 &&
          <div className="questions__container">
            <Category title="Career & Education" />
            { ((formState[0][0].length > 0 && 
              currentQuestionIndex === 1) || currentCategoryIndex > 0) &&
              <Question
                data={DATA_CAREER_EDUCATION_OBJECTIVES}
                selectedAnswers={formState[0][1]}
                categoryIndex={0}
                questionIndex={1}
                selectedCategoryIndex={currentCategoryIndex}
                selectedQuestionIndex={currentQuestionIndex}
                update={updateFormState}
              />
            }
            <Question
              data={DATA_CAREER_ADVANCEMENTS}
              selectedAnswers={formState[0][0]}
              categoryIndex={0}
              questionIndex={0}
              selectedCategoryIndex={currentCategoryIndex}
              selectedQuestionIndex={currentQuestionIndex}
              update={updateFormState}
            />
          </div>
        }
      </div>

      <Modal
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Show My Goals"
        footer={isLoading ? null : undefined}
      >
        <ModalContent
          isFetching={isLoading}
          hasError={error}
        />
      </Modal>
    </div>
  );
};
